import { g as generateSearchUrlBase } from './generate-search-url-base.js';
import { g as getRoomType } from './get-room-type.js';
import { b as SearchPriceType } from './index2.js';

/**
 * **LetsRevenue** (another version of iHotelier/TravelClick by Amadeus)
 *
 * @exports bs_368
 * @category engines
 */
function factory({ window, document, thn }) {

  const engine = {};
  let priceRefresh;
  let onPurchaseEvent;
  let bookingDataObserver;

  /**
   * @memberOf module:bs_368
   * @see {@link prestart}
   *
   * @param {object} ctx - The context object.
   */
  engine.boot = function boot() {
    const willUnmountTools = [1158286, 1040608, 1160038].includes(thn.data.propertyId);
    const onDataLayerPush = ({ message }) => {
      // If not on RnR then we don't need to do anything
      if (!thn.widgets.price.isTargetPage()) {
        return;
      }

      // "ihReservations" is when the user introduces new dates
      if (message.ihReservations) {
        // Specific sites want to unmount tools
        if (willUnmountTools) {
          // When we are on a second/new search
          if (thn.widgets.price.isSet()) {
            thn.reload(1000);
          }
        }
      }

      // "ihResultsDisplayed" is when the results for that dates have been displayed
      if (message.ihResultsDisplayed) {
        thn.widgets.price.refresh();
      }
    };

    thn.bus.on('dataLayer:push', onDataLayerPush, true);

    thn.shutdown(() => {
      thn.bus.detach('dataLayer:push', onDataLayerPush, true);
    });
  };

  /**
     * @memberOf module:bs_368
     * @see {@link is_user_logged}
     *
     * @param {object} ctx - The context object.
     */
  engine.isUserLogged = function isUserLogged(ctx) {
    const isLogged = !document.querySelector('.header-user__sign-in');
    return isLogged;
  };

  /**
     * @memberOf module:bs_368
     * @see {@link language_check}
     *
     * @param {object} ctx - The context object.
     */
  engine.languageCheck = function languageCheck(ctx) {

    return new Promise(function (resolve, reject) {

      if ([1036532, 1036559].includes(thn.data.accountUserId) && !window.location.hostname.match(/ihotelier/)) {
        const lang = document.querySelector('.menu__item--languaje span.valueOfButton')?.textContent?.trim()?.toLowerCase();
        const urlLang = window.location.pathname.match(ctx.urlRe)?.[1];
        if (urlLang && ctx.langs.test(urlLang)) {
          resolve(urlLang);
        } else if (lang && ctx.langs.test(lang)) {
          resolve(lang);
        }
      } else if (window.location.pathname.match(/ihotelier/)) {
        resolve(document.documentElement.lang);
      }

      reject('no_language');
    });
  };
  /**
   * @memberOf module:bs_368
   * @see {@link generate_search_url}
   *
   * @param {object} ctx - The context object.
   */
  engine.generateSearchUrl = function generateSearchUrl(ctx) {
    // example1: https://www.paradoxhotels.com/vancouver/book/accommodations?_ga=2.112693379.663279728.1700140940-1166534872.1700140939&adults=1&datein=11/16/2023&dateout=11/17/2023&domain=www.paradoxhotels.com&languageid=1&rooms=1
    // example 2: https://shadehotel.ihotelier.com/redondo-beach/book/accommodations?thn=true&discount=THNEXCL&adults=2&children=0&rooms=1&DateIn=06%2F19%2F2024&DateOut=06%2F20%2F2024&languageid=en&currency=USD
    const url = new URL(ctx.baseUrl);
    const validUrl = url.pathname.match(/book|reserva/);

    if (!validUrl) {
      return { newUrl: '', newData: {} };
    }

    return generateSearchUrlBase(ctx);
  };

  /**
    *
    * @memberOf module:bs_368
    *
    */
  const roomDetailsParseData = (roomsEl = '') => {
    const roomsArray = roomsEl && [...roomsEl.querySelectorAll('.room-list-wrapper, .room-grid-card-wrapper')];
    return !thn.empty(roomsArray) && roomsArray.map((room) => {
      const roomName = room.querySelector('.room-name')?.getAttribute('aria-label');
      const roomType = getRoomType(roomName);
      const rateList = room.querySelector('amadeus-hos-res-wc-shop-rooms-rate-list')?.shadowRoot;
      const roomPrices = rateList && [...rateList.querySelectorAll('.rate-col')].map((rate) => {
        const rateId = rate.getAttribute('id');
        const rateName = rate.querySelector('.rate-plan-name amadeus-hos-res-ct-title-4')?.getAttribute('aria-label');
        const pricesContainer = rate.querySelector('.rate-section amadeus-hos-res-wc-shop-rooms-rate-display')?.shadowRoot;
        const ratePrice = pricesContainer && thn.parseNumber([...pricesContainer.querySelectorAll('.room-rates span')].pop()?.textContent);
        const hasBreakfast = rateName && (rateName.includes('Breakfast Included') || rateName.includes('Desayuno Incluido'));
        const isRefundable = rateName && rateName.includes('Flexible');
        return {
          id: rateId,
          name: rateName,
          totalNight: ratePrice,
          breakfast: hasBreakfast,
          refundable: isRefundable,
        };
      });

      return {
        id: '',
        name: roomName,
        adults: thn.parseNumber(room.querySelector('.config-item')?.textContent),
        children: 0,
        type: roomType,
        prices: roomPrices,
      };
    });
  };

  /**
   * @memberOf module:bs_368
   * @this thn.widgets.price
   * @see {@link Price#observe_dom}
   *
   * @param {object} ctx - The context object.
   */
  engine.priceObserveDom = function priceObserveDom(ctx) {

    const container = document.querySelector('.ibe-cn-accommodations-page-main-container');

    if (container && !priceRefresh) {

      priceRefresh = thn.debounce(() => {
        if (!thn.isVisible(document.querySelector('.loading-card-image, .ibe-cn-site-loader__icon, .spinner12'))) {
          ctx.widget.refresh();
        }
      }, 1000);

      thn.observeDom(container, priceRefresh, {
        attributes: false,
        children: true,
        subtree: true,
      });

      thn.pageUnload(() => {
        priceRefresh.cancel();
        thn.stopObserveDom(container, priceRefresh);
        priceRefresh = null;
      });
    }
  };

  /**
     * @memberOf module:bs_368
     * @this thn.widgets.price
     * @see {@link Price#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.priceParseData = function priceParseData(ctx) {

    const widgetData = {};
    const roomsData = document.querySelector('amadeus-hos-res-wc-shop-rooms');

    // Dates
    const dateBegin = thn.dateToString(roomsData?.['__amadeus-hos-res-attr-date-in'] || thn.dataLayerHelper.get('ihDate'));
    const dateEnd = thn.dateToString(roomsData?.['__amadeus-hos-res-attr-date-out'] || thn.dataLayerHelper.get('ihDateOut'));

    if (!dateBegin || !dateEnd) {
      return null;
    }

    widgetData.dateBegin = dateBegin;
    widgetData.dateEnd = dateEnd;
    const nights = thn.daysBetween(widgetData.dateBegin, widgetData.dateEnd);

    // Guests, Adults & Children && Prices & Currency || No Rooms
    const adults = roomsData?.['__amadeus-hos-res-attr-adults'];
    const children = roomsData?.['__amadeus-hos-res-attr-children'];
    const childrenAges = roomsData?.['__amadeus-hos-res-attr-child-ages'];

    const roomsEl = roomsData?.shadowRoot;
    let rooms = roomsEl?.querySelectorAll('.row.room-card amadeus-hos-res-wc-shop-rooms-rate-display ')?.length > 0
      ? roomsEl?.querySelectorAll('.row.room-card amadeus-hos-res-wc-shop-rooms-rate-display ')
      : roomsEl?.querySelectorAll('.room-grid-card-wrapper amadeus-hos-res-wc-shop-rooms-rate-display');

    if (!thn.isVisible(roomsData)) {
      rooms = document.querySelector('amadeus-hos-res-wc-shop-rates')?.shadowRoot?.querySelectorAll('amadeus-hos-res-wc-shop-rates-rate-display');
    }
    // We need to exclude rates for some properties
    const excludedRates = /AAA|Government|Senior|AARP|Gov/i;
    const propertiesToBeExcluded = [1045425, 1045426, 1048872, 1048873, 1040968, 1052242, 1036897, 1033882, 1039221, 1030758, 1030751, 1030752].includes(thn.data.propertyId);
    const shouldUseFullPrice = [1157335, 1157337, 1052242].includes(thn.data.propertyId);
    const shouldAvoidMemberRate = [1157335, 1157337].includes(thn.data.propertyId);
    const isUserUsingPriceAfterTaxes = roomsData?.__originalShopRoomTypeList?.[0]?.totalExclusiveTaxes === 0;
    const isUserUsingRatePlans = [1035380].includes(thn.data.accountUserId);
    const pricesArrays = roomsData?.__originalShopRoomTypeList.map((roomType) => (roomType.ratePlans
      ? [...roomType.ratePlans]
        .map((price, index) => {
          if ((shouldAvoidMemberRate && !!price.promoInfo.memberRateTransparent) || (propertiesToBeExcluded && price?.ratePlanName?.match(excludedRates))) {
            return null;
          }
          let priceToDisplay = nights * thn.parseNumber(rooms?.[index]?.shadowRoot?.querySelector('span[itemprop="priceComponent"] meta')?.content);
          if (isUserUsingPriceAfterTaxes) {
            priceToDisplay = price?.totalRateAfterTax;
          } else if (!isUserUsingRatePlans) {
            priceToDisplay = price?.totalRateToDisplay;
          }
          return priceToDisplay;
        })
      : []),
    );
    const prices = !thn.empty(pricesArrays) && [].concat(...pricesArrays).filter(Boolean);

    const currency = thn.parseCurrency(document.querySelector('.header__currency-switcher .cs-trigger span')?.textContent || roomsData?.['amadeus-hos-res-attr-currency']);

    const noAvailability = roomsEl?.querySelector('.amadeus-hos-res-cn-shop-room-total-unavailability-wrapper');

    widgetData.adults = adults || 2;
    widgetData.children = children || 0;
    widgetData.childrenAges = childrenAges || [];

    if (noAvailability) {
      widgetData.noRooms = true;
      return widgetData;
    }

    if (!thn.empty(prices) && currency && nights) {
      widgetData.price = Math.min(...prices) / nights;
      widgetData.priceType = (isUserUsingPriceAfterTaxes || shouldUseFullPrice) ? SearchPriceType.WithTaxes : SearchPriceType.WithoutTaxes;
      widgetData.currency = currency;
      widgetData.roomDetails = roomDetailsParseData(roomsEl);
      widgetData.set = true;
    }

    return widgetData;
  };

  /**
     * @memberOf module:bs_368
     * @this thn.widgets.booking
     * @see {@link Booking#observe_dom}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingObserveDom = function bookingObserveDom(ctx) {
    const bookingSummaryElm = document.querySelector('amadeus-hos-res-wc-booking-summary');

    if (ctx.widget.isSet() || (!bookingSummaryElm && onPurchaseEvent)) {
      return;
    }

    onPurchaseEvent = ({ message }) => {
      if (message?.event === 'ga4_purchase' && ctx.widget.isSet()) {
        ctx.widget.save();
        thn.bus.detach('dataLayer: push', onPurchaseEvent);
        onPurchaseEvent = null;
      }
    };

    thn.bus.on('dataLayer:push', onPurchaseEvent, true);
    thn.pageUnload(() => {
      thn.bus.detach('dataLayer:push', onPurchaseEvent);
      onPurchaseEvent = null;
    });

    bookingDataObserver = thn.debounce(() => {
      if (ctx.widget.setData().isSet()) {
        ctx.widget.save();
        thn.stopObserveDom(bookingSummaryElm, bookingDataObserver);
        bookingDataObserver = null;
      }
    }, 400);

    thn.observeDom(bookingSummaryElm, bookingDataObserver, {
      attributes: true,
      children: true,
      subtree: true,
    });
    thn.pageUnload(() => {
      thn.stopObserveDom(bookingSummaryElm, bookingDataObserver);
      bookingDataObserver = null;
    });
  };

  /**
     * @memberOf module:bs_368
     * @this thn.widgets.booking
     * @see {@link Booking#set_data}
     *
     * @param {object} ctx - The context object.
     */
  engine.bookingParseData = function bookingParseData(ctx) {

    const widgetData = {};
    const bookingDataFromEcommerceDl = thn.dataLayerHelper.get('ecommerce');
    const bookingItemsDataLayer = thn.objectGet(bookingDataFromEcommerceDl, 'items');
    const bookingIdFromDataLayer = bookingItemsDataLayer?.[0]?.[0]?.confirm_id || thn.dataLayerHelper.get('ihBookedConfirmID') || thn.dataLayerHelper.get('ihConfirmID');

    if (bookingIdFromDataLayer) {
      thn.widgets.booking.logger.debug('Booking ID from dataLayer:', window.dataLayer);

      if (bookingItemsDataLayer?.length > 1) {
        thn.widgets.booking.logger.debug('Multiple booking items found in dataLayer');
        const widgetDataMultiBooking = bookingItemsDataLayer.filter((item) => item?.confirm_id).map((item) => ({
          bookingId: item?.confirm_id,
          dateBegin: item?.checkin_date?.replace(/-/g, '/'),
          dateEnd: item?.checkout_date?.replace(/-/g, '/'),
          price: item?.price,
          currency: thn.objectGet(bookingDataFromEcommerceDl, 'currency'),
          promocode: thn.objectGet(bookingDataFromEcommerceDl, 'coupon'),
        }));

        return widgetDataMultiBooking;
      }

      const taxes = thn.objectGet(bookingDataFromEcommerceDl, 'tax') || thn.parseNumber(thn.dataLayerHelper.get('ihTaxes'));
      const priceBeforeTaxes = thn.parseNumber(thn.dataLayerHelper.get('ihAmount'));
      const totalPrice = priceBeforeTaxes && taxes && priceBeforeTaxes + taxes;

      widgetData.bookingId = bookingIdFromDataLayer;
      widgetData.dateBegin = (bookingItemsDataLayer?.[0]?.checkin_date || thn.dataLayerHelper.get('ihDate'))?.replace(/-/g, '/');
      widgetData.dateEnd = (bookingItemsDataLayer?.[0]?.checkout_date || thn.dataLayerHelper.get('ihDateOut'))?.replace(/-/g, '/');
      widgetData.price = bookingItemsDataLayer?.[0]?.price || totalPrice;
      widgetData.currency = thn.objectGet(bookingDataFromEcommerceDl, 'currency') || thn.parseCurrency(thn.dataLayerHelper.get('ihCurrency'));
      widgetData.promocode = thn.objectGet(bookingDataFromEcommerceDl, 'coupon');

      // Oraculo coupons
      const promoCode = thn.dataLayerHelper.get('ecommerce.coupon') || thn.dataLayerHelper.get('ihDiscountCode');
      const isOraculoCode = promoCode?.match(/GMOFFL|GMOFFH|CLOFFL|CLOFFH/i);
      if (isOraculoCode) {
        if (thn.data.accountUserId === 1034117) {
          const oraculoRates = [
            'Special Saving: Best Flexible',
            'Best Flexible',
            'Special Saving: Best Flexible Breakfast',
            'Best Flexible Breakfast',
            'Member Rate: Prepay and Save',
            'Prepay and Save',
            'Member Rate: Prepay and Save Breakfast',
            'Prepay and Save Breakfast',
          ];
          const ratePlan = thn.dataLayerHelper.get('ecommerce.items.0.rate_plan_name') || '';
          widgetData.promocode = oraculoRates.includes(ratePlan) ? promoCode : '';
        }
      } else if (promoCode) {
        widgetData.promocode = promoCode;
      }

      return widgetData;
    }

    const bookingInfoFromAmadeusTag = document.querySelector('amadeus-hos-res-wc-booking-summary')?.getAttribute('amadeus-hos-res-attr-reservations');
    const bookingInfoFromAmadeusTagParsed = bookingInfoFromAmadeusTag && JSON.parse(bookingInfoFromAmadeusTag);
    const bookingIdFromDom = bookingInfoFromAmadeusTagParsed?.[0]?.confirmationNumber;

    thn.widgets.booking.logger.debug('Booking ID from DOM:', bookingInfoFromAmadeusTagParsed);
    const priceFromDom = document.querySelector('amadeus-hos-res-wc-booking-summary')?.shadowRoot?.querySelector('.reservation-total-amount')?.textContent;
    widgetData.bookingId = bookingIdFromDom;
    widgetData.dateBegin = bookingInfoFromAmadeusTagParsed?.[0]?.checkInDate?.replace(/-/g, '/');
    widgetData.dateEnd = bookingInfoFromAmadeusTagParsed?.[0]?.checkOutDate?.replace(/-/g, '/');
    widgetData.price = bookingInfoFromAmadeusTagParsed?.[0]?.roomStays?.[0]?.total?.amountAfterTax || thn.parseNumber(priceFromDom);
    widgetData.currency = thn.parseCurrency(priceFromDom);

    return widgetData;
  };

  /**
   * @memberOf module:bs_368
   * @this thn.widgets.price
   * @see {@link Price#get_promocode}
   *
   * @param {object} ctx - The context object.
   */
  engine.getPromocode = function getPromocode(ctx) {
    const roomsData = document.querySelector('amadeus-hos-res-wc-shop-rooms');
    const promocode = roomsData?.['amadeus-hos-res-attr-access-codes']?.[0]?.code || thn.parseUrlParameter('identifier');
    ctx.promocode = promocode || '';
    return ctx.promocode;
  };

  engine.onBeforeApplyDiscount = async (_ctx) => {
    // The application of discount codes is done via URL for this Booking Engine
    // however the website will not take the new query string parameter
    // unless the reservations information is removed from the session ¯\_(ツ)_/¯
    // In other words, visiting this example URL only works on the first tab load:
    // https://shadehotel.ihotelier.com/redondo-beach/book/accommodations?discount=THNEXCL
    try {
      const { sessionStorage } = window;
      Object.keys(sessionStorage)
        .filter((key) => key.match(/\d+_reservation$/))
        .forEach((key) => {
          sessionStorage.removeItem(key);
        });
    } catch (_) { /* empty */ }
  };

  return engine;

}

export { factory };
